import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlined,
  CloseOutlined,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FamilyRestroomOutlined,
  Inventory2Outlined,
  LuggageOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppShell, SectionCard, TopBar } from '../../design-system';
import { PORTAL_ITEM_ROUTES, PORTAL_ROUTES } from '../../routes';
import {
  requestEmailChangeOtp,
  updateAccountEmail,
  updateAccountPhoneNumber,
} from '../../services/authApi';
import {
  deleteTripAiValidationTask,
  listTripAiValidationTasks,
} from '../../services/tripApi';
import AdminOrdersWorkspace from './AdminOrdersWorkspace';
import DocumentsWorkspace from './DocumentsWorkspace';
import FamilyWorkspace from './FamilyWorkspace';
import OrdersWorkspace from './OrdersWorkspace';
import TripsWorkspace from './TripsWorkspace';

const basePortalItems = [
  {
    id: 'trips',
    label: 'My Trips',
    icon: <LuggageOutlined fontSize="small" />,
    eyebrow: '',
    title: 'My Trips',
    summary: 'Create, review, and track every visa journey from one place.',
    bullets: [
      'Create a new trip with destination, visa type, and travelers.',
      'Track each trip through preparation, review, and submission stages.',
      'Keep trip notes, reminders, and milestones in one place.',
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: <DescriptionOutlined fontSize="small" />,
    eyebrow: '',
    title: 'Documents',
    summary: 'Keep reusable travel documents organized by traveler and status.',
    bullets: [
      'Browse files by family member and document type.',
      'Spot what is missing, expiring, or already approved.',
      'Prepare a shared library future trips can reuse.',
    ],
  },
  {
    id: 'family',
    label: 'Family',
    icon: <FamilyRestroomOutlined fontSize="small" />,
    eyebrow: '',
    title: 'Family',
    summary: 'Store family members profiles once and reuse them across applications.',
    bullets: [
      'Add each family member and keep core profile data together.',
      'Store passport, identity, and relationship details in one record.',
      'Prepare the foundation for dependent-specific workflows.',
    ],
  },
  {
    id: 'account',
    label: 'Account',
    icon: <SettingsOutlined fontSize="small" />,
    eyebrow: '',
    title: 'Account',
    summary: 'Manage your basic details.',
    bullets: [
      'Update contact details and sign-in settings.',
      'Choose notification and communication defaults.',
      'Prepare the account area before full settings logic arrives.',
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: <Inventory2Outlined fontSize="small" />,
    eyebrow: '',
    title: 'Orders',
    summary: 'Track purchases, shipments, and operational follow-up in one place.',
    bullets: [
      'Review service orders and their fulfillment stage.',
      'Follow shipments, courier handoffs, and tracking checkpoints.',
      'Connect operational events back to the right trip and traveler.',
    ],
  },
];

const adminOrdersPortalItem = {
  id: 'admin-orders',
  label: 'Admin Orders',
  icon: <Inventory2Outlined fontSize="small" />,
  eyebrow: 'Operations Admin',
  title: 'Admin Orders',
  summary: 'Review every order across the system, manage fulfillment states, and keep internal notes.',
  bullets: [
    'See every order across all customer accounts.',
    'Filter by status, type, and account to work through queues faster.',
    'Open artifacts, update states, and keep admin-only notes on each order.',
  ],
};

const PHONE_CHANGE_OTP_COOLDOWN_SECONDS = 60;

function getPhoneChangeOtpCooldownStorageKey(phoneNumber) {
  return `account-phone-change-otp-cooldown:${phoneNumber}`;
}

function readPhoneChangeOtpCooldownUntil(phoneNumber) {
  if (!phoneNumber) {
    return 0;
  }

  const storedValue = window.localStorage.getItem(getPhoneChangeOtpCooldownStorageKey(phoneNumber));
  const parsedValue = Number.parseInt(storedValue ?? '0', 10);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function FocusCard({ item }) {
  return (
    <SectionCard
      action={<Chip color="secondary" label="Template" size="small" variant="outlined" />}
      subtitle={item.summary}
      title={item.title}
    >
      <List dense disablePadding>
        {item.bullets.map((bullet) => (
          <ListItem key={bullet} disableGutters sx={{ color: 'text.secondary', py: 0.75 }}>
            <Typography variant="body2">{bullet}</Typography>
          </ListItem>
        ))}
      </List>
    </SectionCard>
  );
}

function AccountSummaryCard({ account, activeItem }) {
  return (
    <SectionCard title="Account">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: 'primary.main', color: 'text.primary' }}>
          {(account?.phoneNumber ?? 'U').slice(-1).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1">{account?.phoneNumber ?? 'Unknown user'}</Typography>
          <Typography color="text.secondary" variant="body2">
            {account?.email ?? 'Email not available yet'}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box>
          <Typography variant="subtitle2">Current section</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            {activeItem.label}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">Status</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Layout is ready for real routes, API data, and section actions.
          </Typography>
        </Box>
      </Box>
    </SectionCard>
  );
}

function AccountSettings({ account, onAccountUpdated }) {
  const [email, setEmail] = useState(account?.email ?? '');
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpRequested, setEmailOtpRequested] = useState(false);
  const [emailOtpCooldownUntil, setEmailOtpCooldownUntil] = useState(0);
  const [emailOtpCooldownSecondsLeft, setEmailOtpCooldownSecondsLeft] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState(account?.phoneNumber ?? '');
  const [emailBusyAction, setEmailBusyAction] = useState('');
  const [phoneBusyAction, setPhoneBusyAction] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const emailIsValid =
    email.trim().length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(email.trim());
  const normalizedPhoneNumber = useMemo(() => phoneNumber.trim(), [phoneNumber]);

  useEffect(() => {
    setEmail(account?.email ?? '');
    setEmailOtpCode('');
    setEmailOtpRequested(false);
    setEmailError('');
    setEmailSuccess('');
  }, [account?.email]);

  useEffect(() => {
    setPhoneNumber(account?.phoneNumber ?? '');
    setPhoneError('');
    setPhoneSuccess('');
  }, [account?.phoneNumber]);

  useEffect(() => {
    setEmailOtpCooldownUntil(readPhoneChangeOtpCooldownUntil(email.trim()));
  }, [email]);

  useEffect(() => {
    if (!emailOtpCooldownUntil) {
      setEmailOtpCooldownSecondsLeft(0);
      return undefined;
    }

    function updateCooldownSecondsLeft() {
      const remainingSeconds = Math.max(0, Math.ceil((emailOtpCooldownUntil - Date.now()) / 1000));
      setEmailOtpCooldownSecondsLeft(remainingSeconds);

      if (remainingSeconds === 0 && email.trim()) {
        window.localStorage.removeItem(getPhoneChangeOtpCooldownStorageKey(email.trim()));
      }
    }

    updateCooldownSecondsLeft();

    const intervalId = window.setInterval(updateCooldownSecondsLeft, 1000);
    return () => window.clearInterval(intervalId);
  }, [email, emailOtpCooldownUntil]);

  const phoneChanged = normalizedPhoneNumber !== (account?.phoneNumber ?? '');
  const emailChanged = email.trim().toLowerCase() !== (account?.email ?? '').toLowerCase();
  const emailCooldownActive = emailOtpCooldownSecondsLeft > 0;

  function startEmailOtpCooldown() {
    const cooldownUntil = Date.now() + PHONE_CHANGE_OTP_COOLDOWN_SECONDS * 1000;
    setEmailOtpCooldownUntil(cooldownUntil);
    window.localStorage.setItem(
      getPhoneChangeOtpCooldownStorageKey(email.trim()),
      String(cooldownUntil)
    );
  }

  async function runEmailAction(actionName, work, onSuccess) {
    setEmailBusyAction(actionName);
    setEmailError('');
    setEmailSuccess('');

    try {
      const result = await work();
      onSuccess?.(result);
    } catch (error) {
      setEmailError(error.message);
    } finally {
      setEmailBusyAction('');
    }
  }

  async function runPhoneAction(actionName, work, onSuccess) {
    setPhoneBusyAction(actionName);
    setPhoneError('');
    setPhoneSuccess('');

    try {
      const result = await work();
      onSuccess?.(result);
    } catch (error) {
      setPhoneError(error.message);
    } finally {
      setPhoneBusyAction('');
    }
  }

  return (
    <SectionCard title="Account Details">
      <Stack spacing={3}>
        {emailError ? <Alert severity="error">{emailError}</Alert> : null}
        {emailSuccess ? <Alert severity="success">{emailSuccess}</Alert> : null}
        {phoneError ? <Alert severity="error">{phoneError}</Alert> : null}
        {phoneSuccess ? <Alert severity="success">{phoneSuccess}</Alert> : null}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <TextField
            error={email.length > 0 && !emailIsValid}
            fullWidth
            helperText={
              email.length > 0 && !emailIsValid ? 'Enter a valid email address.' : ' '
            }
            label="Email address"
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            value={email}
          />
          <Button
            disabled={
              emailBusyAction === 'request-email-change-otp' ||
              emailCooldownActive ||
              !emailIsValid ||
              !emailChanged
            }
            onClick={() =>
              runEmailAction(
                'request-email-change-otp',
                () => requestEmailChangeOtp({ accountId: account.accountId, email }),
                () => {
                  startEmailOtpCooldown();
                  setEmailOtpRequested(true);
                  setEmailSuccess('OTP sent. Enter it below to save your new email.');
                }
              )
            }
            sx={{ minWidth: { md: 140 }, mt: { md: 0.5 } }}
            variant="contained"
          >
            {emailBusyAction === 'request-email-change-otp'
              ? 'Sending OTP...'
              : emailCooldownActive
                ? `Resend in ${emailOtpCooldownSecondsLeft}s`
                : 'Send OTP'}
          </Button>
        </Stack>

        {emailOtpRequested ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <TextField
              inputMode="numeric"
              label="OTP code"
              onChange={(event) => setEmailOtpCode(event.target.value)}
              value={emailOtpCode}
            />
            <Button
              disabled={emailBusyAction === 'complete-email-change' || !emailOtpCode.trim()}
              onClick={() =>
                runEmailAction(
                  'complete-email-change',
                  () => updateAccountEmail({ accountId: account.accountId, email, otpCode: emailOtpCode }),
                  (result) => {
                    onAccountUpdated?.(result);
                    setEmailOtpRequested(false);
                    setEmailOtpCode('');
                    setEmailSuccess('Email updated.');
                  }
                )
              }
              variant="contained"
            >
              {emailBusyAction === 'complete-email-change' ? 'Verifying...' : 'Verify and save'}
            </Button>
          </Box>
        ) : null}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <TextField
            fullWidth
            label="Phone number"
            onChange={(event) => setPhoneNumber(event.target.value)}
            value={phoneNumber}
          />
          <Button
            disabled={phoneBusyAction === 'update-phone-number' || !phoneChanged}
            onClick={() =>
              runPhoneAction(
                'update-phone-number',
                () =>
                  updateAccountPhoneNumber({
                    accountId: account.accountId,
                    newPhoneNumber: normalizedPhoneNumber,
                  }),
                (result) => {
                  onAccountUpdated?.(result);
                  setPhoneSuccess('Phone number updated.');
                }
              )
            }
            sx={{ minWidth: { md: 140 }, mt: { md: 0.5 } }}
            variant="contained"
          >
            {phoneBusyAction === 'update-phone-number' ? 'Saving...' : 'Save phone'}
          </Button>
        </Stack>
      </Stack>
    </SectionCard>
  );
}

function formatBackgroundTaskUpdatedAt(task) {
  const value = task?.completedAtUtc ?? task?.updatedAtUtc ?? task?.createdAtUtc;
  if (!value) {
    return '';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return '';
  }

  return parsedValue.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getBackgroundTaskStatusChipProps(task) {
  if (task.status === 'succeeded') {
    return { color: 'success', label: 'Done', variant: 'filled' };
  }

  if (task.status === 'failed') {
    return { color: 'error', label: 'Failed', variant: 'filled' };
  }

  return {
    color: 'warning',
    label: task.status === 'running' ? 'Running' : 'Pending',
    variant: 'filled',
  };
}

function BackgroundTaskTray({ collapsed, onClearFinished, onDismissTask, onToggle, tasks }) {
  if (!tasks.length) {
    return null;
  }

  const runningCount = tasks.filter((task) => task.status === 'running' || task.status === 'pending').length;
  const finishedCount = tasks.length - runningCount;

  return (
    <Paper
      elevation={8}
      sx={{
        borderRadius: 2,
        bottom: 24,
        overflow: 'hidden',
        position: 'fixed',
        right: 24,
        width: { xs: 'calc(100vw - 32px)', sm: 380 },
        zIndex: (theme) => theme.zIndex.snackbar + 1,
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing={1}
        sx={{
          bgcolor: 'grey.900',
          color: 'common.white',
          px: 1.5,
          py: 1.25,
        }}
      >
        <Typography sx={{ flex: 1, fontWeight: 700 }} variant="subtitle2">
          Background tasks
        </Typography>
        {runningCount ? <Chip color="warning" label={`${runningCount} active`} size="small" /> : null}
        {finishedCount ? (
          <Button color="inherit" onClick={onClearFinished} size="small">
            Clear finished
          </Button>
        ) : null}
        <IconButton color="inherit" onClick={onToggle} size="small">
          {collapsed ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={!collapsed}>
        <Stack spacing={0} sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {tasks.map((task, index) => {
            const statusChipProps = getBackgroundTaskStatusChipProps(task);

            return (
              <Box
                key={task.taskId}
                sx={{
                  borderTop: index === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Stack alignItems="flex-start" direction="row" spacing={1.25}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      color:
                        task.status === 'failed'
                          ? 'error.main'
                          : task.status === 'succeeded'
                            ? 'success.main'
                            : 'warning.main',
                      display: 'flex',
                      height: 24,
                      justifyContent: 'center',
                      mt: 0.25,
                      width: 24,
                    }}
                  >
                    {task.status === 'running' ? (
                      <CircularProgress color="inherit" size={18} thickness={5} />
                    ) : task.status === 'failed' ? (
                      <ErrorOutlineOutlined fontSize="small" />
                    ) : task.status === 'succeeded' ? (
                      <CheckCircleOutlined fontSize="small" />
                    ) : (
                      <ErrorOutlineOutlined fontSize="small" />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={0.75}
                    >
                      <Typography sx={{ fontWeight: 600 }} variant="body2">
                        {task.title}
                      </Typography>
                      <Chip
                        color={statusChipProps.color}
                        label={statusChipProps.label}
                        size="small"
                        variant={statusChipProps.variant}
                      />
                    </Stack>
                    {task.subtitle ? (
                      <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="caption">
                        {task.subtitle}
                      </Typography>
                    ) : null}
                    {task.message ? (
                      <Typography color="text.secondary" sx={{ display: 'block', mt: 0.75 }} variant="body2">
                        {task.message}
                      </Typography>
                    ) : null}
                    <Typography color="text.secondary" sx={{ display: 'block', mt: 0.75 }} variant="caption">
                      {task.status === 'running' || task.status === 'pending' ? 'Started' : 'Updated'} {formatBackgroundTaskUpdatedAt(task)}
                    </Typography>
                  </Box>

                  {task.status !== 'running' && task.status !== 'pending' ? (
                    <IconButton
                      aria-label={`dismiss task ${task.title}`}
                      edge="end"
                      onClick={() => onDismissTask(task.taskId)}
                      size="small"
                    >
                      <CloseOutlined fontSize="small" />
                    </IconButton>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Paper>
  );
}

function getActivePortalItem(pathname, portalItems) {
  if (pathname.startsWith(PORTAL_ROUTES.adminOrders)) {
    return portalItems.find((item) => item.id === 'admin-orders') ?? portalItems[0];
  }

  if (pathname.startsWith(PORTAL_ROUTES.documents)) {
    return portalItems.find((item) => item.id === 'documents') ?? portalItems[0];
  }

  if (pathname.startsWith(PORTAL_ROUTES.family)) {
    return portalItems.find((item) => item.id === 'family') ?? portalItems[0];
  }

  if (pathname.startsWith(PORTAL_ROUTES.account)) {
    return portalItems.find((item) => item.id === 'account') ?? portalItems[0];
  }

  if (pathname.startsWith(PORTAL_ROUTES.orders)) {
    return portalItems.find((item) => item.id === 'orders') ?? portalItems[0];
  }

  return portalItems.find((item) => item.id === 'trips') ?? portalItems[0];
}

function isKnownPortalPath(pathname, isAdmin) {
  if (
    pathname === PORTAL_ROUTES.root ||
    pathname === PORTAL_ROUTES.trips ||
    pathname === PORTAL_ROUTES.documents ||
    pathname === PORTAL_ROUTES.family ||
    pathname === PORTAL_ROUTES.account ||
    pathname === PORTAL_ROUTES.orders
  ) {
    return true;
  }

  if (isAdmin && (pathname === PORTAL_ROUTES.adminRoot || pathname === PORTAL_ROUTES.adminOrders)) {
    return true;
  }

  return false;
}

function PortalShell({ account, onAccountUpdated, onSignOut }) {
  const location = useLocation();
  const navigate = useNavigate();
  const portalItems = useMemo(
    () => (account?.isAdmin ? [...basePortalItems, adminOrdersPortalItem] : basePortalItems),
    [account?.isAdmin]
  );
  const [backgroundTasks, setBackgroundTasks] = useState([]);
  const [backgroundTaskTrayCollapsed, setBackgroundTaskTrayCollapsed] = useState(false);

  const activeItem = useMemo(
    () => getActivePortalItem(location.pathname, portalItems),
    [location.pathname, portalItems]
  );
  const ordersIntent = location.state?.ordersIntent ?? null;

  const primaryItems = portalItems.filter((item) => item.id !== 'account');
  const accountItem = portalItems.find((item) => item.id === 'account');
  const accountDisplayTitle = account?.email ?? 'My Account';
  const accountDisplaySubtitle = account?.phoneNumber ?? 'Signed in';

  useEffect(() => {
    if (location.pathname.startsWith(PORTAL_ROUTES.adminOrders) && !account?.isAdmin) {
      navigate(PORTAL_ROUTES.trips, { replace: true });
    }
  }, [account?.isAdmin, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname === PORTAL_ROUTES.adminRoot) {
      navigate(account?.isAdmin ? PORTAL_ROUTES.adminOrders : PORTAL_ROUTES.trips, { replace: true });
      return;
    }

    if (!isKnownPortalPath(location.pathname, Boolean(account?.isAdmin))) {
      navigate(PORTAL_ROUTES.trips, { replace: true });
    }
  }, [account?.isAdmin, location.pathname, navigate]);

  function handleSelectItem(itemId) {
    const nextRoute = PORTAL_ITEM_ROUTES[itemId];
    if (nextRoute) {
      navigate(nextRoute);
    }
  }

  function handleRequestOrder(intent) {
    navigate(PORTAL_ROUTES.orders, {
      state: {
        ordersIntent: {
          ...intent,
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        },
      },
    });
  }

  function handleConsumeOrdersIntent() {
    if (!location.state?.ordersIntent) {
      return;
    }

    navigate(location.pathname, { replace: true, state: null });
  }

  function dismissBackgroundTask(taskId) {
    if (!account?.token) {
      return;
    }

    void (async () => {
      try {
        await deleteTripAiValidationTask({ token: account.token, taskId });
        setBackgroundTasks((current) => current.filter((task) => task.taskId !== taskId));
      } catch {
        // Keep the task visible if the backend deletion fails.
      }
    })();
  }

  function clearFinishedBackgroundTasks() {
    if (!account?.token) {
      return;
    }

    const finishedTaskIds = backgroundTasks
      .filter((task) => task.status === 'succeeded' || task.status === 'failed')
      .map((task) => task.taskId);

    void Promise.all(finishedTaskIds.map((taskId) => deleteTripAiValidationTask({ token: account.token, taskId }).catch(() => null)))
      .then(() => {
        setBackgroundTasks((current) =>
          current.filter((task) => task.status === 'running' || task.status === 'pending')
        );
      });
  }

  useEffect(() => {
    if (!account?.token) {
      setBackgroundTasks([]);
      return undefined;
    }

    let cancelled = false;

    async function refreshBackgroundTasks() {
      try {
        const response = await listTripAiValidationTasks({ token: account.token });
        if (!cancelled) {
          setBackgroundTasks(Array.isArray(response.tasks) ? response.tasks : []);
        }
      } catch {
        if (!cancelled) {
          setBackgroundTasks((current) => current);
        }
      }
    }

    void refreshBackgroundTasks();
    const intervalId = window.setInterval(refreshBackgroundTasks, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [account?.token]);

  function upsertBackgroundTask(task) {
    setBackgroundTasks((current) => {
      const remainingTasks = current.filter((item) => item.taskId !== task.taskId);
      return [task, ...remainingTasks].sort(
        (left, right) => new Date(right.updatedAtUtc ?? 0).getTime() - new Date(left.updatedAtUtc ?? 0).getTime()
      );
    });
  }

  const backgroundTaskManager = useMemo(
    () => ({
      tasks: backgroundTasks,
      openTray: () => setBackgroundTaskTrayCollapsed(false),
      upsertTask: upsertBackgroundTask,
    }),
    [backgroundTasks]
  );

  return (
    <>
      <AppShell
        bottomAccount={
          accountItem
            ? {
              id: accountItem.id,
              title: accountDisplayTitle,
              subtitle: accountDisplaySubtitle,
              active: accountItem.id === activeItem.id,
            }
            : undefined
        }
        onSelectItem={handleSelectItem}
        onSignOut={onSignOut}
        sections={[
          {
            title: 'Workspace',
            items: primaryItems.map((item) => ({
              id: item.id,
              label: item.label,
              icon: item.icon,
              active: item.id === activeItem.id,
            })),
          },
        ]}
      >
        <TopBar eyebrow={activeItem.eyebrow} subtitle={activeItem.summary} title={activeItem.title} />

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              xl: 'minmax(0, 1.7fr) minmax(320px, 0.9fr)',
            },
          }}
        >
          {activeItem.id === 'account' ? (
            <Box sx={{ gridColumn: { xs: 'auto', xl: '1 / -1' } }}>
              <AccountSettings account={account} onAccountUpdated={onAccountUpdated} />
            </Box>
          ) : activeItem.id === 'trips' ? (
            <Box sx={{ gridColumn: { xs: 'auto', xl: '1 / -1' } }}>
              <TripsWorkspace
                account={account}
                backgroundTaskManager={backgroundTaskManager}
                onRequestOrder={handleRequestOrder}
              />
            </Box>
          ) : activeItem.id === 'documents' ? (
            <Box sx={{ gridColumn: { xs: 'auto', xl: '1 / -1' } }}>
              <DocumentsWorkspace account={account} />
            </Box>
          ) : activeItem.id === 'family' ? (
            <Box sx={{ gridColumn: { xs: 'auto', xl: '1 / -1' } }}>
              <FamilyWorkspace account={account} />
            </Box>
          ) : activeItem.id === 'orders' ? (
            <Box sx={{ gridColumn: { xs: 'auto', xl: '1 / -1' } }}>
              <OrdersWorkspace
                account={account}
                initialIntent={ordersIntent}
                onConsumeIntent={handleConsumeOrdersIntent}
              />
            </Box>
          ) : activeItem.id === 'admin-orders' ? (
            <Box sx={{ gridColumn: { xs: 'auto', xl: '1 / -1' } }}>
              <AdminOrdersWorkspace account={account} />
            </Box>
          ) : (
            <>
              <FocusCard item={activeItem} />

              <AccountSummaryCard account={account} activeItem={activeItem} />

              <Paper
                sx={{
                  gridColumn: { xs: 'auto', xl: '1 / -1' },
                  p: 3,
                }}
              >
                <Typography variant="subtitle1">Quick overview</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                  The shell now follows a lean dashboard pattern: a simpler left rail, a dedicated
                  account entry at the bottom, and a cleaner workspace area for each module.
                </Typography>
              </Paper>
            </>
          )}
        </Box>
      </AppShell>

      <BackgroundTaskTray
        collapsed={backgroundTaskTrayCollapsed}
        onClearFinished={clearFinishedBackgroundTasks}
        onDismissTask={dismissBackgroundTask}
        onToggle={() => setBackgroundTaskTrayCollapsed((current) => !current)}
        tasks={backgroundTasks}
      />
    </>
  );
}

export default PortalShell;
